import { useState, type ReactElement } from "react";
import {
  widgetTargetKey,
  type CausalChainParams,
} from "@playbookai/widget-schemas";
import { Button } from "../../atoms/index.js";
import type { WidgetBodyProps } from "../types.js";
import { useFieldEditSession } from "../field-edit-session.js";
import { newWidgetItemId } from "../widget-item-id.js";
import { CausalChainWidget } from "./Component.js";

export function CausalChainExpandedEditor({
  data,
  onChange,
  readOnly,
}: WidgetBodyProps<CausalChainParams>): ReactElement {
  const [requested, setRequested] = useState(-1);
  const [dirty, setDirty] = useState(false);
  const [revision, setRevision] = useState(0);
  const index =
    requested >= 0 && data.steps[requested]
      ? requested
      : data.problem !== undefined
        ? -1
        : data.steps.length
          ? 0
          : -1;
  const locked = readOnly || dirty;
  const reset = () => {
    setDirty(false);
    setRevision((value) => value + 1);
  };
  const insert = (at: number) => {
    if (locked || data.steps.length >= 20) return;
    const steps = [...data.steps];
    // PB-4923: minted here, because a step without an id cannot be the target
    // of a bounded operation and drops its whole collection back to
    // whole-value replacement.
    steps.splice(at, 0, {
      id: newWidgetItemId("step"),
      label: "New cause",
      body: "",
    });
    onChange({ ...data, steps });
    setRequested(at);
    reset();
  };
  const move = (delta: number) => {
    const to = index + delta;
    if (locked || index < 0 || to < 0 || to >= data.steps.length) return;
    const steps = [...data.steps];
    const [step] = steps.splice(index, 1);
    if (!step) return;
    steps.splice(to, 0, step);
    onChange({ ...data, steps });
    setRequested(to);
    reset();
  };
  const remove = () => {
    if (locked) return;
    if (index < 0) {
      const { problem: _problem, ...rest } = data;
      onChange(rest);
    } else
      onChange({ ...data, steps: data.steps.filter((_, i) => i !== index) });
    setRequested(-1);
    reset();
  };
  return (
    <div className="@container flex min-h-0 flex-1 flex-col gap-3 overflow-auto">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-pb-text-muted">
          {dirty
            ? "Save or cancel edits before selecting another step."
            : "Select a step to edit"}
        </p>
        {!readOnly && (
          <div className="flex gap-2">
            {data.problem === undefined && (
              <Button
                size="sm"
                variant="outline"
                disabled={dirty}
                onClick={() => {
                  onChange({ ...data, problem: "" });
                  setRequested(-1);
                  reset();
                }}
              >
                Add problem
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={dirty || data.steps.length >= 20}
              onClick={() => insert(data.steps.length)}
            >
              Add Why
            </Button>
          </div>
        )}
      </div>
      <div className="grid shrink-0 gap-4 @3xl:min-h-0 @3xl:flex-1 @3xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col @3xl:min-h-0 @3xl:overflow-auto">
          <CausalChainWidget
            data={data}
            onChange={onChange}
            fit
            selection={
              index < 0 ? { kind: "problem" } : { kind: "step", index }
            }
            onSelect={(selection) => {
              if (!dirty) {
                setRequested(
                  selection.kind === "problem" ? -1 : selection.index,
                );
                reset();
              }
            }}
          />
        </div>
        <div className="min-w-0 rounded-lg border border-pb-border p-4 @3xl:overflow-auto">
          {data.problem !== undefined || data.steps.length ? (
            <>
              <DraftEditor
                key={index + ":" + revision}
                data={data}
                index={index}
                readOnly={readOnly === true}
                onChange={onChange}
                onDirty={() => setDirty(true)}
                onDone={reset}
              />
              {!readOnly && (
                <div className="mt-4 flex flex-wrap gap-2 border-t border-pb-border pt-4">
                  {index >= 0 && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={locked || index === 0}
                        onClick={() => move(-1)}
                      >
                        Move up
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={locked || index === data.steps.length - 1}
                        onClick={() => move(1)}
                      >
                        Move down
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={locked || data.steps.length >= 20}
                        onClick={() => insert(index + 1)}
                      >
                        Insert Why below
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={locked}
                    onClick={remove}
                  >
                    {index < 0 ? "Remove problem" : "Delete step"}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-pb-text-muted">
              Add a problem or the first Why to begin.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function DraftEditor({
  data,
  index,
  readOnly,
  onChange,
  onDirty,
  onDone,
}: {
  data: CausalChainParams;
  index: number;
  readOnly: boolean;
  onChange: (next: CausalChainParams) => void;
  onDirty: () => void;
  onDone: () => void;
}): ReactElement {
  const step = data.steps[index];
  const current = {
    label: step?.label ?? "",
    body: index < 0 ? (data.problem ?? "") : (step?.body ?? ""),
    root: step?.isRoot === true,
  };
  const [baseline, setBaseline] = useState(() => JSON.stringify(data));
  const [draft, setDraft] = useState(current);
  const [edited, setEdited] = useState(false);
  const conflict = edited && baseline !== JSON.stringify(data);
  const shown = edited ? draft : current;
  const update = (patch: Partial<typeof draft>) => {
    if (!edited) setBaseline(JSON.stringify(data));
    setDraft({ ...shown, ...patch });
    setEdited(true);
    onDirty();
  };
  const save = () => {
    if (readOnly || conflict || !edited) return;
    if (index < 0) onChange({ ...data, problem: draft.body });
    else
      onChange({
        ...data,
        steps: data.steps.map((item, i) => {
          if (i === index)
            return {
              ...item,
              label: draft.label,
              body: draft.body,
              isRoot: draft.root,
            };
          if (draft.root) {
            const { isRoot: _root, ...rest } = item;
            return rest;
          }
          return item;
        }),
      });
    onDone();
  };
  /*
   * PB-4923 AC5. This one form edits two different things depending on the
   * selection, so the body textarea's target moves with it: at `index < 0` it
   * is the chain's own `problem` field, and on a step it is that step's `body`.
   * Keying it on the step's minted id rather than the index is what survives an
   * insert above it — an ordinal would silently re-point the lease at a
   * neighbour.
   *
   * `CausalStep.id` is `idString.optional()` — PB-4923 added it, and rows
   * written before the backfill still validate without one. Such a step leases
   * NOTHING, and that is the complete answer rather than a fallback: the
   * executor finds an item by `widgetItemIdentity(spec, entry) === leaf.itemId`,
   * which returns `null` for an id-less entry, so no `set_field`, `remove_item`
   * or `move_item` can ever name that step. There is no AI write to defer to.
   * Minting an id here would be strictly worse — a fresh key on every render,
   * naming a target that does not exist.
   */
  const stepTarget = (field: string): string | undefined =>
    step?.id === undefined
      ? undefined
      : widgetTargetKey([
          { kind: "item", collection: "steps", itemId: step.id },
          { kind: "prop", name: field },
        ]);
  const labelLease = useFieldEditSession(stepTarget("label"));
  const bodyLease = useFieldEditSession(
    index < 0 ? "problem" : stepTarget("body"),
  );
  const rootLease = useFieldEditSession(stepTarget("isRoot"));
  const inputClass =
    "w-full rounded-md border border-pb-border bg-pb-card-elevated p-3 text-sm text-pb-text";
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-pb-text">
        {index < 0 ? "Problem statement" : `Edit Why ${index + 1}`}
      </h3>
      {index >= 0 && (
        <label className="block space-y-2 text-sm text-pb-text-muted">
          <span>Label</span>
          <input
            aria-label="Label"
            maxLength={255}
            disabled={readOnly}
            value={shown.label}
            {...labelLease}
            onChange={(event) => update({ label: event.target.value })}
            className={inputClass}
          />
        </label>
      )}
      <label className="block space-y-2 text-sm text-pb-text-muted">
        <span>{index < 0 ? "Problem" : "Explanation"}</span>
        <textarea
          aria-label={index < 0 ? "Problem" : "Explanation"}
          rows={5}
          maxLength={index < 0 ? 255 : 500}
          disabled={readOnly}
          value={shown.body}
          {...bodyLease}
          onChange={(event) => update({ body: event.target.value })}
          className={inputClass}
        />
      </label>
      {index >= 0 && (
        <div>
          <label className="flex items-center gap-2 text-sm text-pb-text">
            <input
              type="checkbox"
              checked={shown.root}
              disabled={readOnly}
              {...rootLease}
              onChange={(event) => update({ root: event.target.checked })}
            />
            Mark as root cause
          </label>
          <p className="mt-2 text-xs text-pb-text-muted">
            A marker, not proof of causation.
          </p>
        </div>
      )}
      {conflict && (
        <p role="alert" className="text-sm text-pb-text">
          The chain changed while you were editing. Cancel to reload the latest
          version.
        </p>
      )}
      {!readOnly && (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          <Button size="sm" disabled={!edited || conflict} onClick={save}>
            Save changes
          </Button>
        </div>
      )}
    </div>
  );
}
